import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Avatar from 'flarum/common/components/Avatar';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Select from 'flarum/common/components/Select';
import Placeholder from 'flarum/common/components/Placeholder';
import username from 'flarum/common/helpers/username';
import extractText from 'flarum/common/utils/extractText';
import Icon from 'flarum/common/components/Icon';
import Tooltip from 'flarum/common/components/Tooltip';
import RankingImage from './RankingImage';
import CountUp from './CountUp';
/**
 * The leaderboard.
 *
 * Reads a dedicated endpoint rather than a sorted list of users: the ranking
 * is computed, so an entry's score and position belong to the metric and
 * period being asked for rather than to the person.
 */
export default class RankingsPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "entries", []);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "loadingMore", false);
    _defineProperty(this, "jumping", false);
    _defineProperty(this, "hasMore", false);
    _defineProperty(this, "metric", void 0);
    _defineProperty(this, "period", void 0);
    /** Where the viewer stands, or null if they are not on the board. */
    _defineProperty(this, "standing", null);
    /** People worth calling out beyond the top three. */
    _defineProperty(this, "highlights", {});
  }
  oninit(vnode) {
    var _this$metrics$;
    super.oninit(vnode);
    if (!app.forum.attribute('canViewRankingPage')) {
      m.route.set('/');
      return;
    }

    // The metric and period are in the URL so a particular board can be linked
    // to and survives a refresh.
    // Falls back to the admin's configured default rather than whichever
    // metric happens to be first in the list.
    this.metric = m.route.param('metric') || app.forum.attribute('fof-gamification.defaultLeaderboardMetric') || ((_this$metrics$ = this.metrics()[0]) == null ? void 0 : _this$metrics$.key) || 'posts';
    this.period = m.route.param('period') || app.forum.attribute('fof-gamification.defaultLeaderboardPeriod') || 'year';
    this.refresh();
  }
  metrics() {
    return app.forum.attribute('fof-gamification.leaderboardMetrics') || [];
  }
  periods() {
    return app.forum.attribute('fof-gamification.leaderboardPeriods') || [];
  }
  currentMetric() {
    return this.metrics().find(metric => metric.key === this.metric);
  }
  refresh() {
    this.loading = true;
    this.entries = [];
    return this.load().then(() => {
      this.loading = false;
      m.redraw();
    });
  }
  load(offset) {
    if (offset === void 0) {
      offset = 0;
    }
    return app.store.find('leaderboard-entries', {
      filter: {
        metric: this.metric,
        period: this.period
      },
      page: {
        offset,
        limit: 20
      }
    }).then(results => {
      var _payload$links, _payload$meta$standin, _payload$meta, _payload$meta$highlig, _payload$meta2;
      this.entries.push(...results);

      // Trust the API's order rather than re-sorting here: sorting each
      // page as it arrives interleaves it with the ones already loaded.
      const payload = results.payload;
      this.hasMore = Boolean(payload == null || (_payload$links = payload.links) == null ? void 0 : _payload$links.next);
      this.standing = (_payload$meta$standin = payload == null || (_payload$meta = payload.meta) == null ? void 0 : _payload$meta.standing) != null ? _payload$meta$standin : null;
      this.highlights = (_payload$meta$highlig = payload == null || (_payload$meta2 = payload.meta) == null ? void 0 : _payload$meta2.highlights) != null ? _payload$meta$highlig : {};
      return results;
    }).catch(() => {
      this.hasMore = false;
    });
  }
  loadMore() {
    this.loadingMore = true;
    this.load(this.entries.length).then(() => {
      this.loadingMore = false;
      m.redraw();
    });
  }
  changeMetric(metric) {
    var _this$currentMetric;
    this.metric = metric;

    // A metric that cannot be limited to a period would silently answer with
    // the all-time figure, so move back to all-time rather than show a period
    // that is not being applied.
    if (!((_this$currentMetric = this.currentMetric()) != null && _this$currentMetric.supportsPeriods)) {
      this.period = 'all';
    }
    this.updateRoute();
  }
  changePeriod(period) {
    this.period = period;
    this.updateRoute();
  }
  updateRoute() {
    m.route.set(app.route('rankings', {
      metric: this.metric,
      period: this.period
    }), undefined, {
      replace: true
    });
    this.refresh();
  }

  /**
   * Page forward until the viewer's own row is loaded, then scroll to it.
   *
   * Someone in 87th place would otherwise have to press "load more" four
   * times to find themselves, which is enough friction that most people will
   * not bother.
   */
  async jumpToMe() {
    if (!this.standing || this.jumping) return;
    this.jumping = true;

    // Keep loading while their position is beyond what has been fetched.
    //
    // Bounded by hasMore as well as by position: a standing computed a moment
    // before the board can disagree with it — a vote landing in between —
    // and without that guard the loop would keep asking for pages that are
    // not there.
    while (this.entries.length < this.standing.position && this.hasMore) {
      await this.load(this.entries.length);
    }
    this.jumping = false;
    m.redraw();

    // After the redraw, so the row exists to scroll to.
    requestAnimationFrame(() => {
      const row = document.getElementById('RankingsPage-me');
      if (row) {
        row.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        return;
      }

      // The board moved under us and the row never arrived. Say so rather
      // than leaving a button that appears to do nothing.
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('fof-gamification.forum.leaderboard.find_me_failed'));
    });
  }
  controls() {
    var _this$currentMetric2;
    const metricOptions = {};
    this.metrics().forEach(metric => {
      metricOptions[metric.key] = app.translator.trans(metric.label);
    });
    const periodOptions = {};
    this.periods().forEach(period => {
      periodOptions[period] = app.translator.trans("fof-gamification.forum.leaderboard.period.".concat(period));
    });
    return m("div", {
      className: "RankingsPage-controls"
    }, m(Select, {
      className: "RankingsPage-metric",
      options: metricOptions,
      value: this.metric,
      onchange: this.changeMetric.bind(this),
      "aria-label": app.translator.trans('fof-gamification.forum.leaderboard.metric_label')
    }), ((_this$currentMetric2 = this.currentMetric()) == null ? void 0 : _this$currentMetric2.supportsPeriods) && m(Select, {
      className: "RankingsPage-period",
      options: periodOptions,
      value: this.period,
      onchange: this.changePeriod.bind(this),
      "aria-label": app.translator.trans('fof-gamification.forum.leaderboard.period_label')
    }), this.standing && m(Button, {
      className: "Button RankingsPage-jump",
      icon: "fas fa-crosshairs",
      onclick: this.jumpToMe.bind(this),
      loading: this.jumping
    }, app.translator.trans('fof-gamification.forum.leaderboard.find_me', {
      position: this.ordinal(this.standing.position)
    })));
  }

  /**
   * The awards that stay winnable.
   *
   * The podium belongs to whoever has been here longest. These belong to
   * whoever showed up this week, which is the only part most members can
   * change — so they are given their own row rather than buried.
   */
  /**
   * The selected period, phrased to sit inside a sentence.
   *
   * "this month" rather than "This month", because it is read as part of an
   * explanation rather than as the label on a control.
   */
  periodName() {
    return extractText(app.translator.trans("fof-gamification.forum.leaderboard.period_phrase.".concat(this.period)));
  }

  /** The window an award is measured against — "last month", not "this month". */
  previousPeriodName() {
    return extractText(app.translator.trans("fof-gamification.forum.leaderboard.period_previous.".concat(this.period)));
  }

  /**
   * The sentence explaining what an award measures.
   */
  highlightHelp(key) {
    return app.translator.trans("fof-gamification.forum.leaderboard.highlight.".concat(key, "_help"), {
      period: this.periodName(),
      previous: this.previousPeriodName()
    });
  }
  highlightStrip() {
    const cards = [{
      key: 'climber',
      icon: 'fas fa-arrow-trend-up',
      value: h => "+".concat(h.places)
    }, {
      key: 'consistent',
      icon: 'fas fa-calendar-check',
      value: h => app.translator.trans('fof-gamification.forum.leaderboard.highlight.days', {
        count: h.days
      })
    }, {
      key: 'newcomer',
      icon: 'fas fa-seedling',
      value: h => {
        var _h$score;
        return (_h$score = h.score) == null ? void 0 : _h$score.toLocaleString();
      }
    }].filter(card => this.highlights[card.key]);
    if (!cards.length) return null;
    return m("ul", {
      className: "RankingsPage-highlights"
    }, cards.map(card => {
      const highlight = this.highlights[card.key];
      return m("li", {
        className: "RankingsPage-highlight RankingsPage-highlight--".concat(card.key),
        key: card.key
      }, m(Icon, {
        name: card.icon,
        className: "RankingsPage-highlightIcon"
      }), m("span", {
        className: "RankingsPage-highlightBody"
      }, m("span", {
        className: "RankingsPage-highlightLabel"
      }, app.translator.trans("fof-gamification.forum.leaderboard.highlight.".concat(card.key))), m(Link, {
        href: app.route('user', {
          username: highlight.slug
        }),
        className: "RankingsPage-highlightName"
      }, highlight.displayName), m("span", {
        className: "RankingsPage-highlightHelp"
      }, this.highlightHelp(card.key))), m("span", {
        className: "RankingsPage-highlightValue"
      }, card.value(highlight)));
    }));
  }

  /**
   * How wide an entry's bar should be, as a percentage.
   *
   * Scaled across the range actually on screen rather than against the
   * leader. The top three are on the podium, so measuring the list against a
   * score that is not in it squeezes every bar into the first third of the
   * track: on a board of 162 down to 66 against a leader of 339, everything
   * lands between 28% and 48% and the differences stop being visible.
   *
   * The floor keeps the last place a bar rather than a sliver, so it still
   * reads as a row with a value.
   */
  barWidth(entry) {
    const listed = this.entries.slice(3).map(e => e.score());
    if (!listed.length) return 100;
    const highest = Math.max(...listed);
    const lowest = Math.min(...listed);

    // Everybody level: a full bar each says that more honestly than an
    // arbitrary fraction would.
    if (highest === lowest) return 100;
    return 8 + (entry.score() - lowest) / (highest - lowest) * 92;
  }

  /**
   * The top three, given room to be the point of the page.
   *
   * A leaderboard exists to make first place feel worth having, and a table
   * renders the leader and the person in tenth identically. These are cards,
   * ordered second-first-third so the winner sits highest and in the middle
   * on a wide screen; the ordering is CSS, so they stack 1-2-3 when the row
   * will not fit.
   */
  podium() {
    const top = this.entries.slice(0, 3);
    if (!top.length) return null;
    return m("ol", {
      className: "RankingsPage-podium RankingsPage-podium--of".concat(top.length)
    }, top.map(entry => {
      const user = entry.user();
      const position = entry.position();
      if (!user) return null;
      return m("li", {
        className: "RankingsPage-podiumPlace RankingsPage-podiumPlace--".concat(position),
        key: entry.id()
      }, m(Link, {
        href: app.route.user(user),
        className: "RankingsPage-podiumLink"
      }, m("span", {
        className: "RankingsPage-podiumMedal"
      }, m(RankingImage, {
        place: position
      })), m(Avatar, {
        user: user,
        className: "RankingsPage-podiumAvatar"
      }), m("span", {
        className: "RankingsPage-podiumName"
      }, username(user)), m("span", {
        className: "RankingsPage-podiumScore"
      }, m(CountUp, {
        value: entry.score()
      })), m("span", {
        className: "RankingsPage-podiumPosition"
      }, this.ordinal(position))));
    }));
  }

  /**
   * Which way somebody is going.
   *
   * The part of a leaderboard that stays winnable: climbing three places is
   * an achievement whoever is top, so it is worth more encouragement than the
   * position itself.
   */
  movement(entry) {
    const raw = entry.movement();
    if (!raw) return null;

    // Arriving on the board is its own event, not a climb from nowhere: it
    // gets a distinct badge rather than a number of places.
    if (raw === 'new') {
      return m(Tooltip, {
        text: extractText(app.translator.trans('fof-gamification.forum.leaderboard.moved_new_tooltip'))
      }, m("span", {
        className: "RankingsPage-movement RankingsPage-movement--new"
      }, app.translator.trans('fof-gamification.forum.leaderboard.moved_new')));
    }
    const moved = parseInt(raw, 10);
    if (!moved) return null;
    const climbed = moved > 0;
    return m(Tooltip, {
      text: extractText(app.translator.trans(climbed ? 'fof-gamification.forum.leaderboard.moved_up' : 'fof-gamification.forum.leaderboard.moved_down', {
        count: Math.abs(moved)
      }))
    }, m("span", {
      className: "RankingsPage-movement RankingsPage-movement--".concat(climbed ? 'up' : 'down')
    }, m(Icon, {
      name: climbed ? 'fas fa-caret-up' : 'fas fa-caret-down'
    }), Math.abs(moved)));
  }

  /**
   * Everybody from fourth down.
   */
  rows() {
    return this.entries.slice(3).map(entry => {
      const user = entry.user();
      const position = entry.position();
      if (!user) return null;
      const viewer = app.session.user;
      const isViewer = Boolean(viewer) && user.id() === viewer.id();
      return m("tr", {
        className: isViewer ? 'RankingsPage-row RankingsPage-row--you' : 'RankingsPage-row',
        key: entry.id(),
        id: isViewer ? 'RankingsPage-me' : undefined
      }, m("td", {
        className: "rankings-place"
      }, m("span", {
        className: "rankings-placeInner"
      }, this.ordinal(position), this.movement(entry))), m("td", {
        className: "rankings-user"
      }, m("div", {
        className: "RankingsPage-identity"
      }, m(Link, {
        href: app.route.user(user),
        className: "RankingsPage-user"
      }, m(Avatar, {
        user: user,
        className: "RankingsPage-avatar"
      }), m("span", {
        className: "RankingsPage-username"
      }, username(user))), isViewer && m("span", {
        className: "RankingsPage-youChip"
      }, app.translator.trans('fof-gamification.forum.leaderboard.you'))), isViewer && this.standing !== null && this.standing.toNext !== null && m("span", {
        className: "RankingsPage-toNext"
      }, app.translator.trans('fof-gamification.forum.leaderboard.to_next', {
        count: this.standing.toNext
      }))), m("td", {
        className: "rankings-bar"
      }, m("span", {
        className: "RankingsPage-bar",
        style: {
          width: "".concat(this.barWidth(entry), "%")
        }
      })), m("td", {
        className: "rankings-score"
      }, entry.score().toLocaleString()));
    });
  }

  /**
   * The position as an ordinal.
   *
   * Delegated to Intl so that every locale gets its own rule rather than
   * English suffixes being appended to numbers in languages that do not use
   * them.
   */
  ordinal(position) {
    const locale = app.data.locale || 'en';
    try {
      const rules = new Intl.PluralRules(locale, {
        type: 'ordinal'
      });
      return app.translator.trans("fof-gamification.forum.leaderboard.ordinal.".concat(rules.select(position)), {
        count: position
      });
    } catch (_unused) {
      // A locale Intl does not know: the bare number is still correct, just
      // not decorated.
      return String(position);
    }
  }
  view() {
    return m(PageStructure, {
      className: "RankingsPage",
      hero: () => IndexPage.prototype.hero(),
      sidebar: () => m(IndexSidebar, null)
    }, this.controls(), this.loading ? m(LoadingIndicator, null) : this.entries.length === 0 ? m(Placeholder, {
      text: app.translator.trans('fof-gamification.forum.leaderboard.empty')
    }) : m('[', null, this.podium(), this.highlightStrip(), this.entries.length > 3 && m("table", {
      className: "rankings"
    }, m("thead", null, m("tr", null, m("th", {
      scope: "col",
      className: "rankings-place"
    }, app.translator.trans('fof-gamification.forum.ranking.rank')), m("th", {
      scope: "col",
      className: "rankings-user"
    }, app.translator.trans('fof-gamification.forum.ranking.name')), m("th", {
      scope: "col",
      className: "rankings-bar",
      "aria-hidden": "true"
    }), m("th", {
      scope: "col",
      className: "rankings-score"
    }, this.currentMetric() ? app.translator.trans(this.currentMetric().label) : app.translator.trans('fof-gamification.forum.ranking.amount')))), m("tbody", null, this.rows())), this.hasMore && m("div", {
      className: "rankings-loadmore"
    }, this.loadingMore ? m(LoadingIndicator, null) : m(Button, {
      className: "Button",
      onclick: this.loadMore.bind(this)
    }, app.translator.trans('core.forum.discussion_list.load_more_button')))));
  }
}
flarum.reg.add('fof-gamification', 'forum/components/RankingsPage', RankingsPage);